import { log } from './logger';

/**
 * Enhanced timezone utility functions that build on the existing timezoneUtils.js
 * Provides more intelligent and user-centric timezone handling
 */

import { formatDualTimezone, isUserInDifferentTimezone } from './timezoneUtils';

/**
 * Smart timezone formatter that adapts based on context and user preferences
 * @param {string} timestamp - Event timestamp
 * @param {string} eventTimezone - Event timezone 
 * @param {Object} options - Formatting options
 * @returns {Object} Formatted time with context-aware display
 */
export const smartFormatTime = (timestamp, eventTimezone, options = {}) => {
  const {
    context = 'default', // 'list', 'detail', 'notification', 'email'
    showRelativeTime = false,
    preferredFormat = 'auto', // 'auto', 'event-only', 'user-only', 'dual'
    compactMode = false,
    showDate = false // Add support for showDate option
  } = options;

  if (!timestamp) {
    return { formatted: 'Not specified', isEmpty: true };
  }

  const baseFormat = formatDualTimezone(timestamp, eventTimezone);
  const userInDifferentTz = isUserInDifferentTimezone(eventTimezone);
  const date = new Date(timestamp);
  const now = new Date();

  // Calculate relative time information
  const timeDiff = date - now;
  const isUpcoming = timeDiff > 0;
  const isPast = timeDiff < 0;
  const absTimeDiff = Math.abs(timeDiff);
  
  const minutes = Math.floor(absTimeDiff / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  // Generate relative time string
  let relativeTime = '';
  if (showRelativeTime) {
    if (minutes < 60) {
      relativeTime = isUpcoming ? `in ${minutes} min` : `${minutes} min ago`;
    } else if (hours < 24) {
      relativeTime = isUpcoming ? `in ${hours}h` : `${hours}h ago`;
    } else if (days < 7) {
      relativeTime = isUpcoming ? `in ${days}d` : `${days}d ago`;
    }
  }

  // Context-based formatting
  let contextualFormat = baseFormat.time;
  
  switch (context) {
    case 'list':
      // Compact format for list views
      if (compactMode && !userInDifferentTz) {
        contextualFormat = baseFormat.eventTime.replace(/:\d{2}/, ''); // Remove minutes if :00
      }
      break;
      
    case 'detail':
      // Full format for detail views
      if (showDate) {
        contextualFormat = `${baseFormat.date} at ${baseFormat.time}`;
      } else {
        contextualFormat = baseFormat.time;
      }
      if (relativeTime) {
        contextualFormat += ` (${relativeTime})`;
      }
      break;
      
    case 'notification':
      // User-friendly format for notifications
      if (userInDifferentTz) {
        contextualFormat = `${baseFormat.userTime} (${baseFormat.eventTime.split(' ').pop()})`; // Show user time with event timezone abbr
      } else {
        contextualFormat = baseFormat.eventTime;
      }
      break;
      
    case 'email':
      // Clear, unambiguous format for emails
      contextualFormat = `${baseFormat.date} at ${baseFormat.time}`;
      break;
    
    default:
      // Default format 
      contextualFormat = baseFormat.time;
      break;
  }

  // Apply preferred format override
  if (preferredFormat === 'event-only') {
    contextualFormat = `${baseFormat.date} at ${baseFormat.eventTime}`;
  } else if (preferredFormat === 'user-only' && baseFormat.userTime) {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const userDateFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: userTimezone,
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    const userDate = userDateFormatter.format(date);
    contextualFormat = `${userDate} at ${baseFormat.userTime}`;
  }

  return {
    formatted: contextualFormat,
    date: baseFormat.date,
    time: contextualFormat,
    eventTime: baseFormat.eventTime,
    userTime: baseFormat.userTime,
    relativeTime,
    isUpcoming,
    isPast,
    timeDiff: absTimeDiff,
    context,
    isMultiTimezone: userInDifferentTz,
    isEmpty: false
  };
};

/**
 * Format time range with intelligent timezone handling
 * @param {string} startTime - Start timestamp
 * @param {string} endTime - End timestamp  
 * @param {string} eventTimezone - Event timezone
 * @param {Object} options - Formatting options
 * @returns {string} Formatted time range
 */
export const formatTimeRange = (startTime, endTime, eventTimezone, options = {}) => {
  const { compactMode = false } = options;
  
  if (!startTime || !endTime) {
    return 'Time not specified';
  }

  try {
    const userInDifferentTz = isUserInDifferentTimezone(eventTimezone);
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);
    
    // Check if same day
    const sameDay = startDate.toDateString() === endDate.toDateString();
    
    const timeOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    
    // Format start and end times in event timezone
    const startEventFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: eventTimezone,
      ...timeOptions
    });
    
    const endEventFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: eventTimezone,
      ...timeOptions,
      ...(compactMode ? {} : { timeZoneName: 'short' })
    });
    
    const startEventTime = startEventFormatter.format(startDate);
    const endEventTime = endEventFormatter.format(endDate);
    
    // Build event time range
    let eventRange;
    if (sameDay) {
      eventRange = `${startEventTime} - ${endEventTime}`;
    } else {
      const startDateStr = new Intl.DateTimeFormat('en-US', {
        timeZone: eventTimezone,
        month: 'short',
        day: 'numeric'
      }).format(startDate);
      const endDateStr = new Intl.DateTimeFormat('en-US', {
        timeZone: eventTimezone,
        month: 'short',
        day: 'numeric'
      }).format(endDate);
      eventRange = `${startDateStr} ${startEventTime} - ${endDateStr} ${endEventTime}`;
    }
    
    // Add user timezone if different
    if (userInDifferentTz && !compactMode) {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const startUserTime = new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        ...timeOptions
      }).format(startDate);
      const endUserTime = new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        ...timeOptions,
        timeZoneName: 'short'
      }).format(endDate);
      
      const userRange = sameDay ? `${startUserTime} - ${endUserTime}` : 
                                 `${startUserTime} - ${endUserTime}`;
      eventRange += ` (${userRange})`;
    }
    
    return eventRange;
  } catch (err) {
      // Error caught and ignored
    log.error('Error formatting time range:', err);
    return 'Invalid time range';
  }
};

/**
 * Get timezone-aware event status
 * @param {string} startTime - Event start time
 * @param {string} endTime - Event end time
 * @param {string} eventTimezone - Event timezone
 * @returns {Object} Event status information
 */
export const getEventStatus = (startTime, endTime, _eventTimezone) => {
  if (!startTime) {
    return { status: 'unknown', label: 'Time not set', color: 'gray' };
  }

  try {
    const now = new Date();
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : null;
    
    // Calculate time difference
    const timeDiff = start - now;
    const hours = timeDiff / (1000 * 60 * 60);
    
    if (end && now > end) {
      return { status: 'finished', label: 'Finished', color: 'gray' };
    } else if (end && now >= start && now <= end) {
      return { status: 'happening', label: 'Happening now', color: 'green' };
    } else if (hours <= 1 && hours > 0) {
      return { status: 'starting-soon', label: 'Starting soon', color: 'orange' };
    } else if (hours <= 24 && hours > 1) {
      return { status: 'today', label: 'Today', color: 'blue' };
    } else if (timeDiff > 0) {
      return { status: 'upcoming', label: 'Upcoming', color: 'blue' };
    } else {
      return { status: 'past', label: 'Past', color: 'gray' };
    }
  } catch (err) {
      // Error caught and ignored
    log.error('Error getting event status:', err);
    return { status: 'unknown', label: 'Unknown', color: 'gray' };
  }
};

/**
 * Convert timestamp between timezones while preserving the intended time
 * @param {string} timestamp - Original timestamp
 * @param {string} fromTimezone - Source timezone
 * @param {string} toTimezone - Target timezone  
 * @returns {Object} Converted timestamp information
 */
export const convertTimezone = (timestamp, fromTimezone, toTimezone) => {
  if (!timestamp) return null;
  
  try {
    const date = new Date(timestamp);
    
    // Format in target timezone
    const targetFormatter = new Intl.DateTimeFormat('sv-SE', { // ISO format
      timeZone: toTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const targetISOString = targetFormatter.format(date).replace(' ', 'T') + '.000Z';
    
    return {
      timestamp: targetISOString,
      date: new Date(targetISOString),
      timezone: toTimezone,
      formatted: smartFormatTime(targetISOString, toTimezone).formatted
    };
  } catch (err) {
      // Error caught and ignored
    log.error('Error converting timezone:', err);
    return null;
  }
};

/**
 * Batch format multiple events with consistent timezone handling
 * @param {Array} events - Array of event objects with timestamps
 * @param {string} defaultTimezone - Default timezone for events
 * @param {Object} options - Formatting options
 * @returns {Array} Events with formatted time information
 */
export const batchFormatEvents = (events, defaultTimezone = 'America/New_York', options = {}) => {
  const { context = 'list', groupByDate = false } = options;
  
  if (!Array.isArray(events)) return [];
  
  const formattedEvents = events.map(event => {
    const eventTimezone = event.timezone || defaultTimezone;
    const startTime = smartFormatTime(event.starts_at || event.start_time, eventTimezone, { context });
    const endTime = event.ends_at || event.end_time ? 
                   smartFormatTime(event.ends_at || event.end_time, eventTimezone, { context }) : 
                   null;
    
    const timeRange = (event.starts_at || event.start_time) && (event.ends_at || event.end_time) ?
                     formatTimeRange(event.starts_at || event.start_time, 
                                   event.ends_at || event.end_time, 
                                   eventTimezone, 
                                   { compactMode: context === 'list' }) : null;
    
    const status = getEventStatus(event.starts_at || event.start_time, 
                                 event.ends_at || event.end_time, 
                                 eventTimezone);
    
    return {
      ...event,
      timezone: eventTimezone,
      formatted: {
        startTime,
        endTime,
        timeRange,
        status
      }
    };
  });
  
  if (groupByDate) {
    // Group events by date in user's timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const grouped = {};
    
    formattedEvents.forEach(event => {
      if (event.starts_at || event.start_time) {
        const date = new Date(event.starts_at || event.start_time);
        const dateKey = new Intl.DateTimeFormat('en-CA', { 
          timeZone: userTimezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).format(date);
        
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(event);
      }
    });
    
    return grouped;
  }
  
  return formattedEvents;
};